import { useCallback, useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { mt, MBtn, MCard, fmtT } from "./shared";

// Mobile field reporting (§94) — reuses the EXISTING RMS report system end-to-end:
// same drafts, sections, validation, attachments and submission. Nothing duplicated.
const INC_FIELDS = [["incident_type", "Incident Type"], ["location_name", "Location Name"],
  ["district", "District"], ["beat", "Beat"]];

function useTalkToText(onText) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const toggle = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Talk-to-text not supported in this browser"); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.continuous = true; rec.interimResults = false; rec.lang = "en-US";
    rec.onresult = (e) => {
      const txt = Array.from(e.results).slice(e.resultIndex).map((r) => r[0].transcript).join(" ");
      if (txt.trim()) onText(txt.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec; rec.start(); setListening(true);
  };
  return [listening, toggle];
}

function ReportEditor({ rid, onBack }) {
  const [rep, setRep] = useState(null);
  const [validation, setValidation] = useState(null);
  const [saveState, setSaveState] = useState("saved"); // saved | dirty | saving
  const [uploading, setUploading] = useState(false);
  const [certify, setCertify] = useState(false);
  const dirtyRef = useRef({}); // {section: data}
  const narrativeRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [r, v] = await Promise.all([
        client.get(`/rms-reports/${rid}`), client.get(`/rms-reports/${rid}/validate`)]);
      setRep(r.data); setValidation(v.data);
      narrativeRef.current = r.data.narrative_sections;
    } catch { toast.error("Could not load report"); }
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  // §94 auto-save: debounce dirty sections to the server every 2.5s.
  useEffect(() => {
    const t = setInterval(async () => {
      const dirty = dirtyRef.current;
      const keys = Object.keys(dirty);
      if (!keys.length) return;
      dirtyRef.current = {};
      setSaveState("saving");
      try {
        for (const section of keys) {
          await client.patch(`/rms-reports/${rid}/section`, { section, data: dirty[section] });
        }
        setSaveState("saved");
        client.get(`/rms-reports/${rid}/validate`).then((v) => setValidation(v.data)).catch(() => {});
      } catch { setSaveState("dirty"); Object.assign(dirtyRef.current, dirty); }
    }, 2500);
    return () => clearInterval(t);
  }, [rid]);

  const editField = (section, key, value) => {
    setRep((r) => ({ ...r, [section]: { ...r[section], [key]: value } }));
    dirtyRef.current[section] = { ...(dirtyRef.current[section] || {}), [key]: value };
    setSaveState("dirty");
  };
  const editNarrative = (idx, text) => {
    setRep((r) => {
      const ns = r.narrative_sections.map((s, i) => (i === idx ? { ...s, text } : s));
      dirtyRef.current.narrative_sections = ns;
      return { ...r, narrative_sections: ns };
    });
    setSaveState("dirty");
  };
  const [listening, toggleTalk] = useTalkToText((txt) => {
    setRep((r) => {
      const idx = r.narrative_sections.length - 1;
      const ns = r.narrative_sections.map((s, i) =>
        (i === idx ? { ...s, text: (s.text ? s.text + " " : "") + txt } : s));
      dirtyRef.current.narrative_sections = ns;
      return { ...r, narrative_sections: ns };
    });
    setSaveState("dirty");
  });

  const upload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      fd.append("description", "Captured from mobile field device");
      await client.post(`/rms-reports/${rid}/attachments`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} photo(s) attached (GPS/time metadata in audit)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    try {
      await client.post(`/rms-reports/${rid}/submit`, { certify: true });
      toast.success("Report submitted for supervisor review");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };

  if (!rep) return <div className="p-6 font-mono text-sm" style={{ color: mt.dim }}>Loading report…</div>;
  const editable = ["DRAFT", "RETURNED"].includes(rep.status);
  const pct = validation ? Math.max(0, Math.round(100 - (validation.errors?.length || 0) * 12)) : 0;

  return (
    <div data-testid="mobile-report-editor" className="flex flex-col gap-3 p-3 pb-28">
      <div className="flex items-center justify-between">
        <span className="font-head font-bold text-sm" style={{ color: mt.text }}>{rep.case_number}</span>
        <MBtn small outline color={mt.dim} onClick={onBack} testid="mobile-report-back">BACK</MBtn>
      </div>
      {/* Progress / sync strip */}
      <MCard testid="mobile-report-progress">
        <div className="flex items-center justify-between text-[11px] font-mono">
          <span style={{ color: mt.dim }}>STATUS: <b style={{ color: mt.text }}>{rep.status}</b></span>
          <span data-testid="mobile-report-savestate"
            style={{ color: saveState === "saved" ? mt.green : saveState === "saving" ? mt.cyan : mt.amber }}>
            {saveState === "saved" ? "● SAVED" : saveState === "saving" ? "● SAVING…" : "● UNSAVED (auto-save on)"}
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: mt.surface }}>
          <div style={{ width: `${pct}%`, height: "100%", background: pct > 80 ? mt.green : mt.amber }} />
        </div>
        {validation?.errors?.length > 0 && (
          <div className="text-[10px] font-mono mt-1.5" style={{ color: mt.amber }}>
            Missing: {validation.errors.slice(0, 3).join(" · ")}{validation.errors.length > 3 ? ` +${validation.errors.length - 3} more` : ""}
          </div>
        )}
      </MCard>

      {/* Incident section */}
      <MCard testid="mobile-report-incident">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>INCIDENT</div>
        <div className="text-[11px] font-mono mb-2" style={{ color: mt.dim }}>
          CAD {rep.cad_number} · {rep.incident?.call_type} · {rep.incident?.address}
        </div>
        {editable && INC_FIELDS.map(([k, label]) => (
          <input key={k} data-testid={`mobile-report-inc-${k}`} value={rep.incident?.[k] || ""}
            onChange={(e) => editField("incident", k, e.target.value)} placeholder={label}
            className="w-full mb-2 px-3 py-2.5 rounded-lg font-mono text-sm"
            style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
        ))}
      </MCard>

      {/* Narrative + talk-to-text */}
      <MCard testid="mobile-report-narrative">
        <div className="flex items-center justify-between mb-2">
          <span className="font-head font-bold text-xs" style={{ color: mt.text }}>NARRATIVE</span>
          {editable && (
            <MBtn small color={listening ? mt.red : mt.purple} onClick={toggleTalk} testid="mobile-report-talk">
              {listening ? "■ STOP DICTATION" : "🎙 TALK-TO-TEXT"}
            </MBtn>
          )}
        </div>
        {(rep.narrative_sections || []).map((s, i) => (
          <div key={i} className="mb-2">
            <div className="text-[10px] font-mono uppercase mb-1" style={{ color: mt.dim }}>{s.title || `Section ${i + 1}`}</div>
            <textarea data-testid={`mobile-report-narr-${i}`} value={s.text || ""} disabled={!editable}
              onChange={(e) => editNarrative(i, e.target.value)} rows={4}
              className="w-full px-3 py-2.5 rounded-lg font-mono text-sm"
              style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
          </div>
        ))}
      </MCard>

      {/* Camera / attachments */}
      <MCard testid="mobile-report-camera">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>
          PHOTOS & EVIDENCE ({(rep.attachments || []).length})
        </div>
        {(rep.attachments || []).map((a, i) => (
          <div key={i} className="text-[11px] font-mono py-1" style={{ color: mt.dim }}>
            📎 {a.filename || a.name || `attachment ${i + 1}`} · {fmtT(a.ts || a.uploaded_at)}
          </div>
        ))}
        {editable && (
          <>
            <input ref={fileRef} type="file" accept="image/*,video/*" capture="environment" multiple
              className="hidden" onChange={upload} data-testid="mobile-report-file-input" />
            <MBtn full color={mt.cyan} disabled={uploading} onClick={() => fileRef.current?.click()}
              testid="mobile-report-capture">
              {uploading ? "UPLOADING…" : "📷 CAPTURE PHOTO / VIDEO"}
            </MBtn>
          </>
        )}
      </MCard>

      {/* Certify + submit */}
      {editable && (
        <MCard testid="mobile-report-submit-card">
          <label className="flex items-start gap-2 mb-3">
            <input type="checkbox" checked={certify} onChange={(e) => setCertify(e.target.checked)}
              data-testid="mobile-report-certify" className="mt-1" />
            <span className="text-[11px] font-mono" style={{ color: mt.dim }}>
              I certify this report is true and accurate to the best of my knowledge. (You — not AI — certify the facts.)
            </span>
          </label>
          <MBtn full color={mt.green} disabled={!certify} onClick={submit} testid="mobile-report-submit">
            SUBMIT TO SUPERVISOR
          </MBtn>
        </MCard>
      )}
    </div>
  );
}

export function MobileReports({ myUnit, activeIncidentCc, onBack }) {
  const [reports, setReports] = useState([]);
  const [openRid, setOpenRid] = useState(null);
  const load = useCallback(() => {
    client.get("/rms-reports", { params: { mine: true } }).then((r) => setReports(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const startFromIncident = async () => {
    if (!activeIncidentCc) { toast.error("No active incident assigned to you."); return; }
    try {
      const r = await client.post(`/rms-reports/from-incident/${activeIncidentCc}`);
      toast.success(r.data.existing ? "Opened existing draft" : `Draft ${r.data.case_number} created from CAD`);
      setOpenRid(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start report"); }
  };

  if (openRid) return <ReportEditor rid={openRid} onBack={() => { setOpenRid(null); load(); }} />;
  return (
    <div data-testid="mobile-reports" className="flex flex-col gap-3 p-3 pb-28">
      <div className="flex items-center justify-between">
        <span className="font-head font-bold text-sm" style={{ color: mt.text }}>FIELD REPORTS</span>
        <MBtn small outline color={mt.dim} onClick={onBack} testid="mobile-reports-back">BACK</MBtn>
      </div>
      <MBtn full color={mt.blue} onClick={startFromIncident} testid="mobile-reports-start">
        + START REPORT FROM CURRENT CALL {activeIncidentCc ? `(${activeIncidentCc})` : ""}
      </MBtn>
      {reports.length === 0 && <MCard><span className="text-sm font-mono" style={{ color: mt.dim }}>No reports yet.</span></MCard>}
      {reports.map((r) => (
        <MCard key={r.id} testid={`mobile-report-row-${r.case_number}`}>
          <button className="w-full text-left" onClick={() => setOpenRid(r.id)} data-testid={`mobile-report-open-${r.case_number}`}>
            <div className="flex items-center justify-between">
              <span className="font-head font-bold text-sm" style={{ color: mt.text }}>{r.case_number}</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-head font-bold"
                style={{ background: r.status === "DRAFT" ? mt.amber : r.status === "RETURNED" ? mt.red : mt.green, color: "#fff" }}>
                {r.status}
              </span>
            </div>
            <div className="text-[11px] font-mono mt-0.5" style={{ color: mt.dim }}>
              CAD {r.cad_number} · {r.incident?.call_type} · {fmtT(r.created_at)}
            </div>
          </button>
        </MCard>
      ))}
    </div>
  );
}
